import { useMutation, useQuery } from '@tanstack/react-query'
import { ContactUnitsFilterParams, CreateContactUnitParams, UpdateContactUnitParams } from '@/types/contact-unit'
import { useContactUnitsStore } from '@/store'
import { SortOrderEnum } from '@/enums'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'

export const useContactUnits = (params: ContactUnitsFilterParams) => {
  const fetchContactUnits = useContactUnitsStore(state => state.fetchContactUnits)

  return useQuery({
    queryKey: ['contact-units', params],
    queryFn: () => fetchContactUnits(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })
}

export const useContactUnit = (id: string) => {
  const getContactUnit = useContactUnitsStore(state => state.getContactUnit)

  return useQuery({
    queryKey: ['contact-unit', id],
    queryFn: () => getContactUnit(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id
  })
}

export const useCreateContactUnit = () => {
  const createContactUnit = useContactUnitsStore(state => state.createContactUnit)

  return useMutation({
    mutationFn: (params: CreateContactUnitParams) => createContactUnit(params),
    onSuccess: () => {}
  })
}

export const useUpdateContactUnit = () => {
  const updateContactUnit = useContactUnitsStore(state => state.updateContactUnit)

  return useMutation({
    mutationFn: ({ id, params }: { id: string; params: UpdateContactUnitParams }) => updateContactUnit(id, params),
    onSuccess: () => {}
  })
}

export const useSortContactUnit = () => {
  const sortContactUnit = useContactUnitsStore(state => state.sortContactUnit)

  return useMutation({
    mutationFn: ({ id, type }: { id: string; type: SortOrderEnum }) => sortContactUnit(id, type),
    onSuccess: () => {}
  })
}

export const useDeleteContactUnit = () => {
  const deleteContactUnit = useContactUnitsStore(state => state.deleteContactUnit)

  return useMutation({
    mutationFn: (id: string) => deleteContactUnit(id),
    onSuccess: () => {}
  })
}

export const useBulkDeleteContactUnits = () => {
  const bulkDeleteContactUnits = useContactUnitsStore(state => state.bulkDeleteContactUnits)
  const deselectAllContactUnits = useContactUnitsStore(state => state.deselectAllContactUnits)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteContactUnits(ids),
    onSuccess: response => {
      deselectAllContactUnits()
      handleBulkDeleteSuccess(response)
    }
  })
}
